import { useCallback, useEffect, useState } from 'react';
import { Check, Pencil, Plus, RotateCcw, Trash2, X } from 'lucide-react';
import { apiAffaireTasks } from '../api/client.js';
import { invalidateTaskCache } from './TaskPicker.jsx';

/**
 * Ticket #76 — administration des tâches partagées d'une affaire.
 *
 * La création à la volée vit dans `TaskPicker`, au plus près de la saisie :
 * c'est le geste Float qu'il fallait rétablir, et il n'a rien à faire ici.
 * Cet onglet couvre ce que le picker ne peut pas offrir — corriger une
 * orthographe, et retirer de la liste une tâche qu'on n'utilise plus.
 *
 * **Renommer, pas recréer** : tout pointe sur l'id de la tâche, donc le
 * nouveau libellé se propage aux allocations, aux imputations et au reporting
 * sans rien détacher. C'est l'intérêt d'avoir remplacé le texte libre par une
 * entité.
 *
 * **Archiver, pas supprimer** : la tâche sort des listes déroulantes mais les
 * saisies déjà imputées dessus gardent leur rattachement, sinon l'historique
 * de reporting deviendrait illisible.
 *
 * Props :
 *  - affaireId : id de l'affaire
 *  - canEdit : booléen — false rend l'onglet en lecture seule
 *  - onCountChange : (count) => void, pour le compteur du tab parent
 */

const inputStyle = {
  width: '100%',
  padding: '8px 10px',
  background: 'var(--glass)',
  color: 'var(--text)',
  border: '1px solid var(--glass-border)',
  borderRadius: 'var(--radius-sm)',
  fontSize: 14,
};

const primaryBtn = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  background: 'var(--accent)',
  color: '#fff',
  border: 'none',
  borderRadius: 'var(--radius-pill)',
  padding: '8px 16px',
  fontSize: 13,
  fontWeight: 500,
  cursor: 'pointer',
};

const iconBtn = {
  background: 'transparent',
  border: 'none',
  padding: 4,
  borderRadius: 'var(--radius-sm)',
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const cellStyle = { padding: '8px 10px', color: 'var(--text)', fontSize: 13 };

export default function AffaireTasksTab({ affaireId, canEdit, onCountChange }) {
  const [tasks, setTasks] = useState([]);
  const [archivedTasks, setArchivedTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showArchived, setShowArchived] = useState(false);

  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);
  // Renommage en place : un champ texte dans la ligne plutôt qu'une modale —
  // corriger une faute de frappe ne vaut pas un aller-retour modal.
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState('');
  const [busyId, setBusyId] = useState(null);

  const reload = useCallback(async () => {
    if (!affaireId) return;
    setLoading(true);
    setError(null);
    try {
      const [active, archived] = await Promise.all([
        apiAffaireTasks.list(affaireId, 'active'),
        apiAffaireTasks.list(affaireId, 'archived'),
      ]);
      setTasks(active.items ?? []);
      setArchivedTasks(archived.items ?? []);
      onCountChange?.(active.items?.length ?? 0);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [affaireId, onCountChange]);

  useEffect(() => {
    reload();
  }, [reload]);

  // Toute mutation invalide le cache du picker : sans ça, une tâche renommée
  // ici continuerait d'apparaître sous son ancien libellé dans les modales de
  // saisie déjà ouvertes.
  const afterMutation = useCallback(async () => {
    invalidateTaskCache(affaireId);
    await reload();
  }, [affaireId, reload]);

  const onCreate = async (e) => {
    e.preventDefault();
    const name = newName.trim();
    if (!name) return;
    setCreating(true);
    setError(null);
    try {
      await apiAffaireTasks.create(affaireId, name);
      setNewName('');
      await afterMutation();
    } catch (err) {
      setError(err.message);
    } finally {
      setCreating(false);
    }
  };

  const onRename = async (task) => {
    const name = editingName.trim();
    if (!name || name === task.name) {
      setEditingId(null);
      return;
    }
    setBusyId(task.id);
    setError(null);
    try {
      await apiAffaireTasks.rename(task.id, name);
      setEditingId(null);
      await afterMutation();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyId(null);
    }
  };

  const onArchive = async (task) => {
    const used = task.usageCount ?? 0;
    if (
      !window.confirm(
        used > 0
          ? `Retirer « ${task.name} » de la liste ?\n\n${used} allocation(s) et saisie(s) y sont rattachées : elles gardent leur tâche, mais elle ne sera plus proposée.`
          : `Retirer « ${task.name} » de la liste ?`,
      )
    ) {
      return;
    }
    setBusyId(task.id);
    setError(null);
    try {
      await apiAffaireTasks.archive(task.id);
      await afterMutation();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyId(null);
    }
  };

  const onRestore = async (task) => {
    setBusyId(task.id);
    setError(null);
    try {
      await apiAffaireTasks.restore(task.id);
      await afterMutation();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyId(null);
    }
  };

  const renderRow = (task, archived) => {
    const isEditing = editingId === task.id;
    const busy = busyId === task.id;
    return (
      <tr key={task.id} style={{ borderTop: '1px solid var(--glass-border)' }}>
        <td style={cellStyle}>
          {isEditing ? (
            <input
              type="text"
              value={editingName}
              autoFocus
              disabled={busy}
              onChange={(e) => setEditingName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  onRename(task);
                } else if (e.key === 'Escape') {
                  e.stopPropagation();
                  setEditingId(null);
                }
              }}
              style={inputStyle}
            />
          ) : (
            <span style={{ opacity: archived ? 0.6 : 1 }}>{task.name}</span>
          )}
        </td>
        <td style={{ ...cellStyle, color: 'var(--text-muted)', textAlign: 'right' }}>
          {task.usageCount ?? 0}
        </td>
        <td style={{ ...cellStyle, textAlign: 'right', whiteSpace: 'nowrap' }}>
          {!canEdit ? null : isEditing ? (
            <>
              <button
                type="button"
                onClick={() => onRename(task)}
                disabled={busy}
                title="Valider le nouveau nom"
                style={iconBtn}
              >
                <Check size={16} color="var(--success)" />
              </button>
              <button
                type="button"
                onClick={() => setEditingId(null)}
                disabled={busy}
                title="Annuler"
                style={iconBtn}
              >
                <X size={16} color="var(--text-muted)" />
              </button>
            </>
          ) : archived ? (
            <button
              type="button"
              onClick={() => onRestore(task)}
              disabled={busy}
              title="Remettre la tâche dans la liste"
              style={iconBtn}
            >
              <RotateCcw size={16} color="var(--success)" />
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={() => {
                  setEditingId(task.id);
                  setEditingName(task.name);
                }}
                disabled={busy}
                title="Renommer la tâche"
                style={iconBtn}
              >
                <Pencil size={16} />
              </button>
              <button
                type="button"
                onClick={() => onArchive(task)}
                disabled={busy}
                title="Retirer la tâche de la liste"
                style={iconBtn}
              >
                <Trash2 size={16} color="var(--danger)" />
              </button>
            </>
          )}
        </td>
      </tr>
    );
  };

  if (loading) {
    return <div style={{ padding: 16, color: 'var(--text-muted)' }}>Chargement…</div>;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <p style={{ margin: 0, fontSize: 12, color: 'var(--text-muted)' }}>
        Ces tâches sont partagées : elles apparaissent dans la liste déroulante de tous
        les collaborateurs de l'affaire, au planning comme à la saisie de temps. Chacun
        peut en créer une à la volée depuis ces écrans ; cet onglet sert à corriger un
        libellé ou à retirer une tâche devenue inutile.
      </p>

      {error ? (
        <div style={{ color: 'var(--danger)', fontSize: 13 }}>{error}</div>
      ) : null}

      {canEdit ? (
        <form onSubmit={onCreate} style={{ display: 'flex', gap: 8 }}>
          <input
            type="text"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Nom de la nouvelle tâche"
            disabled={creating}
            style={inputStyle}
          />
          <button
            type="submit"
            disabled={creating || !newName.trim()}
            style={{ ...primaryBtn, opacity: creating || !newName.trim() ? 0.5 : 1 }}
          >
            <Plus size={14} /> Ajouter
          </button>
        </form>
      ) : null}

      {tasks.length === 0 ? (
        <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>
          Aucune tâche sur cette affaire.
        </div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: 'var(--glass)', textAlign: 'left' }}>
              <th style={{ ...cellStyle, color: 'var(--text-muted)', fontWeight: 500 }}>Tâche</th>
              <th
                style={{
                  ...cellStyle,
                  color: 'var(--text-muted)',
                  fontWeight: 500,
                  textAlign: 'right',
                }}
                title="Allocations et saisies rattachées à cette tâche"
              >
                Usages
              </th>
              <th style={{ ...cellStyle, width: 90 }} />
            </tr>
          </thead>
          <tbody>{tasks.map((t) => renderRow(t, false))}</tbody>
        </table>
      )}

      {archivedTasks.length > 0 ? (
        <div>
          <button
            type="button"
            onClick={() => setShowArchived((v) => !v)}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'var(--text-muted)',
              cursor: 'pointer',
              fontSize: 12,
              padding: 0,
            }}
          >
            {showArchived ? 'Masquer' : 'Afficher'} les {archivedTasks.length} tâche(s)
            retirée(s)
          </button>
          {showArchived ? (
            <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 8 }}>
              <tbody>{archivedTasks.map((t) => renderRow(t, true))}</tbody>
            </table>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
